import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Plus, Edit, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const emptyForm = {
  code: '', name: '', name_ar: '', vat_number: '',
  mobile: '', email: '', address: '', contact_person: '', notes: ''
};

/**
 * QuickAddAccountDialog
 * Shared dialog for adding/editing customer or supplier accounts.
 * Props:
 *   open, onOpenChange, type ('customer'|'supplier'), mode ('add'|'edit'),
 *   editData (account object for edit mode), organizationId, onSaved(account)
 */
const QuickAddAccountDialog = ({
  open, onOpenChange, type = 'customer', mode = 'add',
  editData = null, organizationId, onSaved,
}) => {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [loadingCode, setLoadingCode] = useState(false);

  const isCustomer = type === 'customer';
  const prefix = isCustomer ? '4111' : '4011';
  const label = isCustomer ? 'Customer' : 'Supplier';
  const accountType = isCustomer ? 'asset' : 'liability';
  const vatPrefix = isCustomer ? '4114' : '4014';

  useEffect(() => {
    if (!open) return;
    if (mode === 'edit' && editData) {
      setForm({
        code: editData.code || '',
        name: editData.name || '',
        name_ar: editData.name_ar || '',
        vat_number: editData.vat_number || '',
        mobile: editData.mobile || '',
        email: editData.email || '',
        address: editData.address || '',
        contact_person: editData.contact_person || '',
        notes: editData.notes || '',
      });
    } else {
      setForm(emptyForm);
      if (mode === 'add' && organizationId) {
        fetchNextCode();
      }
    }
  }, [open, mode, editData]);

  const fetchNextCode = async () => {
    setLoadingCode(true);
    try {
      const res = await axios.get(`${API}/accounts/next-code?organization_id=${organizationId}&prefix=${prefix}`);
      setForm(f => ({ ...f, code: res.data.next_code || '' }));
    } catch { /* user can type manually */ }
    finally { setLoadingCode(false); }
  };

  const handleSave = async () => {
    if (mode === 'add') {
      if (!form.code || !form.name.trim()) {
        alert('Code and Name are required');
        return;
      }
      if (!form.code.startsWith(prefix) || form.code.length <= 4) {
        alert(`${label} code must start with "${prefix}" and have more than 4 digits`);
        return;
      }
      setSaving(true);
      try {
        const res = await axios.post(`${API}/accounts`, {
          code: form.code,
          name: form.name.trim(),
          name_ar: form.name_ar || null,
          account_class: 4,
          account_type: accountType,
          parent_code: null,
          is_active: true,
          organization_id: organizationId,
          mobile: form.mobile || null,
          email: form.email || null,
          address: form.address || null,
          contact_person: form.contact_person || null,
          notes: form.notes || null,
          vat_number: form.vat_number || null,
        });
        if (onSaved) onSaved(res.data);
        onOpenChange(false);
      } catch (err) {
        alert(err.response?.data?.detail || `Failed to create ${label.toLowerCase()}`);
      } finally { setSaving(false); }
    } else {
      // Edit mode — update contact info
      if (!editData?.id) return;
      setSaving(true);
      try {
        const res = await axios.put(`${API}/accounts/${editData.id}/contact-info`, {
          mobile: form.mobile || '',
          address: form.address || '',
          contact_person: form.contact_person || '',
          email: form.email || '',
          notes: form.notes || '',
          vat_number: form.vat_number || '',
        });
        if (onSaved) onSaved(res.data);
        onOpenChange(false);
      } catch (err) {
        alert(err.response?.data?.detail || `Failed to update ${label.toLowerCase()}`);
      } finally { setSaving(false); }
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {mode === 'add' ? <Plus className="w-5 h-5 text-primary" /> : <Edit className="w-5 h-5 text-primary" />}
            {mode === 'add' ? `Add New ${label}` : `Edit ${label}`}
          </DialogTitle>
          {mode === 'add' && (
            <DialogDescription>
              Create a new {label.toLowerCase()} account ({prefix}xxxx). A VAT mirror account ({vatPrefix}xxxx) will be auto-created.
            </DialogDescription>
          )}
          {mode === 'edit' && editData && (
            <DialogDescription>
              Update contact information for {editData.name}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="space-y-4">
          {/* Code + VAT — only in Add mode (code is not editable in edit) */}
          {mode === 'add' ? (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Account Code *</Label>
                <div className="relative">
                  <Input
                    placeholder={`e.g. ${prefix}01`}
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value })}
                    className="font-mono"
                    data-testid={`qa-${type}-code`}
                    disabled={loadingCode}
                  />
                  {loadingCode && <Loader2 className="w-4 h-4 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />}
                </div>
                <p className="text-[10px] text-muted-foreground">Must start with {prefix} + digits</p>
              </div>
              <div className="space-y-2">
                <Label>VAT Number</Label>
                <Input placeholder="VAT number..." value={form.vat_number}
                  onChange={(e) => setForm({ ...form, vat_number: e.target.value })}
                  data-testid={`qa-${type}-vat`} />
              </div>
            </div>
          ) : (
            <div className="p-3 bg-muted/30 rounded-sm">
              <p className="font-mono text-sm text-primary">{editData?.code}</p>
              <p className="font-medium">{editData?.name}</p>
            </div>
          )}

          {/* Name EN/AR — only in Add mode */}
          {mode === 'add' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name (English) *</Label>
                <Input placeholder={`${label} name...`} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid={`qa-${type}-name`} />
              </div>
              <div className="space-y-2">
                <Label>Name (Arabic)</Label>
                <Input placeholder={isCustomer ? 'اسم الزبون...' : 'اسم المورد...'} value={form.name_ar}
                  onChange={(e) => setForm({ ...form, name_ar: e.target.value })} dir="rtl" />
              </div>
            </div>
          )}

          {/* Editable contact fields (both modes) */}
          {mode === 'edit' && (
            <div className="space-y-2">
              <Label>VAT Number</Label>
              <Input placeholder="VAT number..." value={form.vat_number}
                onChange={(e) => setForm({ ...form, vat_number: e.target.value })} />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Contact Person</Label>
              <Input placeholder="Contact person..." value={form.contact_person}
                onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Mobile</Label>
              <Input placeholder="Phone number..." value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" placeholder="Email..." value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Address</Label>
              <Input placeholder="Address..." value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea placeholder="Additional notes..." value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={`qa-${type}-cancel`}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid={`qa-${type}-save`}>
            {saving ? <><Loader2 className="w-4 h-4 animate-spin mr-1" />Saving...</> : mode === 'add' ? `Create ${label}` : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default QuickAddAccountDialog;
